import { Box, LoadingOverlay, Pagination, Table } from "@mantine/core"
import { useDebouncedValue } from '@mantine/hooks'
import { ListState } from "../ui/state-views"
import React, { JSX, useImperativeHandle } from "react"
import useSwr from "swr"
import { IFilter } from '../../models/Filter'
import { Sort } from '../table'
import classes from "./custom-table.module.css"
export * from "../table/row-action"

type Props = {
  name: string
  tbody: (data: any[]) => JSX.Element
  thead: JSX.Element
  filters?: { [key: string]: string | number | null | (string | number | Date | null)[] }
  pagination?: boolean
  dataSource?: any[]
  loadData?: (filter?: IFilter) => Promise<any>
  limit?: number
  onResult?: any
  sorted?: [string, Sort]
  /** Custom empty-state title (no rows and no active filter). */
  emptyTitle?: string
  /** Second line under the empty title (canvas: what will appear here). */
  emptyDescription?: string
  /** Primary action of the empty state, e.g. «Шинэ захиалга». */
  emptyAction?: { label: string; onClick: () => void }
  /** @dehub/help: custom empty content per state (EmptyStateGuide); return null to keep the default ListState. */
  renderEmpty?: (state: "empty" | "noResults") => React.ReactNode
  /** Shown with the «no results» state so the user can reset the filters. */
  onClearFilters?: () => void
  /** Whether a USER-set filter is active (empty vs «no results»). Omitted: derived from `filters` minus `baseFilterKeys`. */
  hasFilter?: boolean
  /** Fixed filter keys the page always sends (not chosen by the user). */
  baseFilterKeys?: string[]
  /** Keep the header row visible above the empty / no-results state (Ord_DropShip). */
  headWhenEmpty?: boolean
}

/** Keys the list pages send as fixed scope, never as a user choice. */
const DEFAULT_BASE_FILTER_KEYS = ["type", "isDropshipping"]

/** A filter counts as active when any value is non-empty (arrays with at least one non-null entry). */
function hasActiveFilter(filters: Props["filters"], baseKeys: string[]): boolean {
  return Object.entries(filters ?? {}).filter(([k]) => !baseKeys.includes(k)).map(([, v]) => v).some((v) =>
    Array.isArray(v) ? v.some((x) => x !== null && x !== undefined && x !== "") : v !== null && v !== undefined && v !== "",
  )
}

export type ITableRef = { reload: () => void }

function CustomTable({ name, filters = {}, pagination = true, loadData, limit = 20, onResult, thead, tbody, sorted, emptyTitle, emptyDescription, emptyAction, renderEmpty, onClearFilters, hasFilter, baseFilterKeys = DEFAULT_BASE_FILTER_KEYS, headWhenEmpty = false }: Props, ref: React.Ref<ITableRef>) {
  const scrollRef = React.useRef<HTMLDivElement>(null)
  const tableRef = React.useRef<HTMLTableElement>(null)
  const [offset, setOffset] = React.useState({ page: 1, limit: limit || 20 })
  const [fallbackData, setFallbackData] = React.useState<{
    count: number
    rows: any[]
  }>({ count: 0, rows: [] })
  const [debouncedFilters] = useDebouncedValue(filters, 1000)
  const [debouncedSorted] = useDebouncedValue(sorted, 1000)

  const { data, mutate, isLoading, error } = useSwr(
    // Key on the debounced values the fetcher uses, so typing does not refetch the previous filters.
    ['table', name, offset, debouncedFilters, debouncedSorted],
    async () => {
      if (loadData) {
        let res = await loadData({
          offset,
          filter: debouncedFilters,
          order: debouncedSorted
        } as IFilter)
        setFallbackData(res)
        return res
      }
      return { count: 0, rows: [] }
    },
    {
      fallbackData: fallbackData,
      onSuccess: (data) => {
        onResult && onResult(data)
        if (scrollRef.current) {
          scrollRef.current.scrollTo({ top: 0 })
        }
      },
    },
  )

  useImperativeHandle(ref, () => ({
    reload() {
      return mutate()
    },
  }))

  return (
    <Box className={classes.container}>
      <Box ref={scrollRef} onScroll={() => { }} className={classes.scroll}>
        <Table
          ref={tableRef}
          style={{
            minWidth: 1000,
          }}>
          {/* Std: the header shows only with rows (or their skeleton). */}
          {!error && (data?.rows?.length > 0 || isLoading || (headWhenEmpty && data?.count === 0)) && thead}
          {data?.rows?.length > 0 && tbody(data.rows)}
        </Table>

        {error ? (
          <ListState state="error" onRetry={() => mutate()} errorCode={(error as { status?: number })?.status} />
        ) : isLoading && !(data?.rows?.length > 0) ? (
          <ListState state="loading" columns={6} />
        ) : data?.count === 0 ? (
          (hasFilter ?? hasActiveFilter(filters, baseFilterKeys)) ? (
            renderEmpty?.("noResults") ?? <ListState state="noResults" action={onClearFilters ? { onClick: onClearFilters } : undefined} />
          ) : (
            renderEmpty?.("empty") ?? <ListState state="empty" title={emptyTitle} description={emptyDescription} action={emptyAction} />
          )
        ) : null}

        {/* Reload over existing rows: the overlay covers the table only, filters stay usable. */}
        <LoadingOverlay visible={isLoading && data?.rows?.length > 0} />

        {pagination && !error && data?.count > 0 && (
          <Box className={classes.pagination}>
            <Pagination
              onChange={(page) => setOffset((state) => ({ ...state, page: page }))}
              total={data.count / offset.limit + (data.count % offset.limit > 0 ? 1 : 0)}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const CustomTableComponent = React.forwardRef(CustomTable)

export { CustomTableComponent as CustomsTable }
